import React, { useCallback, useEffect, useState } from "react";
import { Plus, ShieldCheck, Trash2 } from "@/components/icons";
import { DeleteConfirm } from "@/components/DeleteConfirm";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { useUnsavedGuard, DiscardDialog } from "@/components/useUnsavedGuard";
import { InspectionStatusBadge, formatNlDate } from "@/components/Badges";
import { Card } from "@/components/ui/card";
import { InlineEmpty } from "@/components/ListState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const RESPONSIBLE_LABELS = { ons: "Ons", verhuurder: "Verhuurder", extern: "Extern" };

/**
 * InspectionsPanel — lists + manages inspections for a location or an asset.
 * Pass exactly one of locationId / assetId. isVehicle=true enables the APK note.
 *
 * Round 28: the type picklist reads the managed register (/inspection-types) as
 * OBJECTS, so the kenteken field and behaviour hang on the type's `voertuig`
 * flag, never on the literal name "APK".
 */
export const InspectionsPanel = ({ locationId, assetId, isVehicle, title = "Keuringen" }) => {
  const { canWrite } = useAuth();
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const EMPTY = { name: "", type_id: null, frequency_months: "12", last_date: "", license_plate: "", responsible: "ons", notes: "" };
  const [form, setForm] = useState(EMPTY);
  const guard = useUnsavedGuard(JSON.stringify(form) !== JSON.stringify(EMPTY), () => setOpen(false));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = locationId ? `location_id=${locationId}` : `asset_id=${assetId}`;
      const [ins, ty] = await Promise.all([api.get(`/inspections?${q}`), api.get("/inspection-types")]);
      setItems(ins.data); setTypes(ty.data);
    } catch (e) { toast.error(getErrorMessage(e, "Kan keuringen niet laden.")); }
    finally { setLoading(false); }
  }, [locationId, assetId]);
  useEffect(() => { load(); }, [load]);

  const selectedType = types.find((t) => t.naam === form.name) || null;
  const showPlate = Boolean(isVehicle || selectedType?.voertuig);

  const onPickType = (naam) => {
    const t = types.find((x) => x.naam === naam) || null;
    setForm((f) => ({
      ...f,
      name: naam,
      type_id: t?.id || null,
      frequency_months: t?.standaard_frequentie_maanden ? String(t.standaard_frequentie_maanden) : f.frequency_months,
    }));
  };

  const submit = async () => {
    if (!form.name) { toast.error("Kies een keuringstype."); return; }
    setSaving(true);
    try {
      await api.post("/inspections", {
        name: form.name,
        type_id: form.type_id,
        location_id: locationId || null,
        asset_id: assetId || null,
        frequency_months: Number(form.frequency_months) || 12,
        last_date: form.last_date || null,
        license_plate: form.license_plate || null,
        responsible: form.responsible,
        notes: form.notes,
      });
      toast.success("Keuring toegevoegd");
      setOpen(false);
      setForm(EMPTY);
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Opslaan mislukt.")); }
    finally { setSaving(false); }
  };

  const verify = async (id) => {
    try { await api.post(`/inspections/${id}/verify`); toast.success("Gemarkeerd als gecontroleerd"); await load(); }
    catch (e) { toast.error(getErrorMessage(e, "Mislukt.")); }
  };
  const remove = async (id) => {
    try { await api.delete(`/inspections/${id}`); toast.success("Keuring verwijderd"); await load(); }
    catch (e) { toast.error(getErrorMessage(e, "Verwijderen mislukt.")); }
  };

  return (
    <Card className="p-5" data-testid="inspections-panel">
      <div className="mb-3 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="flex items-center gap-2 text-body font-semibold text-foreground"><ShieldCheck size={18} /> {title}</h2>
        {canWrite() && (
          <Button size="sm" data-testid="inspection-add-button" onClick={() => { setForm(EMPTY); setOpen(true); }}><Plus size={18} className="mr-2" /> Keuring toevoegen</Button>
        )}
      </div>

      {loading ? (
        <p className="text-body text-muted-foreground">Laden...</p>
      ) : items.length === 0 ? (
        <InlineEmpty
          testid="inspections-empty"
          title="Nog geen keuringen vastgelegd"
          purpose="Leg per keuring vast wanneer hij voor het laatst is gedaan en hoe vaak hij moet. Het systeem zet de volgende datum in de kalender en waarschuwt voordat hij verloopt."
          action={canWrite() ? { label: "Keuring toevoegen", icon: Plus, onClick: () => { setForm(EMPTY); setOpen(true); } } : null}
        />
      ) : (
        <>
        <div className="hidden sm:block">
          <Table data-testid="inspections-table">
            <TableHeader>
              <TableRow>
                <TableHead>Keuring</TableHead>
                <TableHead>Laatste</TableHead>
                <TableHead>Volgende</TableHead>
                <TableHead>Verantwoordelijk</TableHead>
                <TableHead>Status</TableHead>
                {canWrite() && <TableHead className="text-right">Actie</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((i) => (
                <TableRow key={i.id} data-testid={`inspection-row-${i.id}`}>
                  <TableCell className="text-body">{i.name}{i.license_plate ? ` (${i.license_plate})` : ""}</TableCell>
                  <TableCell className="text-body">{i.last_date ? formatNlDate(i.last_date) : ""}</TableCell>
                  <TableCell className="text-body">{i.next_due ? formatNlDate(i.next_due) : ""}</TableCell>
                  <TableCell className="text-body">{RESPONSIBLE_LABELS[i.responsible] || i.responsible}</TableCell>
                  <TableCell><InspectionStatusBadge status={i.status} /></TableCell>
                  {canWrite() && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {i.status === "te_controleren" && (
                          <Button size="sm" variant="outline" data-testid={`inspection-verify-${i.id}`} onClick={() => verify(i.id)}>Bevestigen</Button>
                        )}
                        <DeleteConfirm
                          trigger={<Button size="icon" variant="ghost" data-testid={`inspection-delete-${i.id}`}><Trash2 size={18} /></Button>}
                          title={`Keuring ${i.name || ""} verwijderen?`.replace(/\s+\?/, "?")}
                          description="Dit kan niet ongedaan gemaakt worden."
                          onConfirm={() => remove(i.id)}
                          testid={`inspection-delete-confirm-${i.id}`}
                        />
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Phone: one card per keuring. */}
        <div className="divide-y divide-border sm:hidden" data-testid="inspections-cardlist">
          {items.map((i) => (
            <div key={i.id} data-testid={`inspection-card-${i.id}`} className="flex items-start justify-between gap-3 py-3">
              <div className="min-w-0 space-y-0.5">
                <div className="text-body font-medium text-foreground">{i.name}{i.license_plate ? ` (${i.license_plate})` : ""}</div>
                <div className="text-data text-muted-foreground">
                  {[i.last_date ? `laatst ${formatNlDate(i.last_date)}` : null,
                    i.next_due ? `volgende ${formatNlDate(i.next_due)}` : null,
                    RESPONSIBLE_LABELS[i.responsible] || i.responsible].filter(Boolean).join(" · ")}
                </div>
                <div className="pt-1"><InspectionStatusBadge status={i.status} /></div>
              </div>
              {canWrite() && (
                <div className="flex shrink-0 items-center gap-1">
                  {i.status === "te_controleren" && (
                    <Button size="sm" variant="outline" data-testid={`inspection-card-verify-${i.id}`} onClick={() => verify(i.id)}>Bevestigen</Button>
                  )}
                  <DeleteConfirm
                    trigger={<Button size="icon" variant="ghost" data-testid={`inspection-card-delete-${i.id}`}><Trash2 size={18} /></Button>}
                    title={`Keuring ${i.name || ""} verwijderen?`.replace(/\s+\?/, "?")}
                    description="Dit kan niet ongedaan gemaakt worden."
                    onConfirm={() => remove(i.id)}
                    testid={`inspection-card-delete-confirm-${i.id}`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        </>
      )}

      <Dialog open={open} onOpenChange={guard.requestClose}>
        <DialogContent srDescription="Leg een keuring vast: type, frequentie en de laatste keuringsdatum.">
          <DiscardDialog guard={guard} />
          <DialogHeader><DialogTitle>Keuring toevoegen</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.name} onValueChange={onPickType}>
                <SelectTrigger data-testid="inspection-type-select"><SelectValue placeholder="Kies keuringstype" /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.naam}>{t.naam}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Frequentie (maanden)</Label><Input type="number" min="1" data-testid="inspection-frequency-input" value={form.frequency_months} onChange={(e) => setForm((f) => ({ ...f, frequency_months: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Laatste keuring</Label><Input type="date" data-testid="inspection-lastdate-input" value={form.last_date} onChange={(e) => setForm((f) => ({ ...f, last_date: e.target.value }))} /></div>
            </div>
            {showPlate && (
              <div className="space-y-1.5"><Label>Kenteken</Label><Input data-testid="inspection-plate-input" value={form.license_plate} onChange={(e) => setForm((f) => ({ ...f, license_plate: e.target.value }))} placeholder="12-ABC-3" /></div>
            )}
            <div className="space-y-1.5">
              <Label>Verantwoordelijk</Label>
              <Select value={form.responsible} onValueChange={(v) => setForm((f) => ({ ...f, responsible: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ons">Ons</SelectItem><SelectItem value="verhuurder">Verhuurder</SelectItem><SelectItem value="extern">Extern</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Notities</Label><Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => guard.requestClose(false)}>Annuleren</Button>
            <Button data-testid="inspection-save-button" onClick={submit} disabled={saving}>{saving ? "Bezig..." : "Opslaan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
